import React from 'react';
import classNames from 'classnames';
import {Link, NavLink, useNavigate} from 'react-router-dom';
import {
    ChevronDownIcon,
    CircleHelpIcon,
    CloudIcon,
    CompassIcon,
    LayoutGridIcon,
    LogOutIcon,
    MenuIcon,
    PlugIcon,
    PlusIcon,
    SettingsIcon,
    ShieldUserIcon,
    UserRoundIcon,
    UsersIcon,
    type LucideIcon
} from 'lucide-react';
import Icon from '../shared/Icon.tsx';
import {useAuthStateManager} from '../../hooks/useAuthStateManager.tsx';
import useClientConfig from '../../hooks/useClientConfig.tsx';
import {UserStatus} from '../../api/types.ts';
import RoleNames from '../../lib/RoleNames.ts';
import SiteContentConstants from '../../lib/SiteContentConstants.ts';
import joinPath from '../../lib/joinPath.ts';
import profileUrl from '../../lib/profileUrl.ts';
import atify from '../../lib/atify.ts';
import MxNotifications from '../notifications/MxNotifications.tsx';
import {Avatar} from './CreatorIdentity.tsx';
import Menu from './Menu.tsx';
import SidePanel from './SidePanel.tsx';
import {useSidePanel} from './sidePanelContext.ts';

interface Destination {
    to: string;
    label: string;
    icon: LucideIcon;
    end?: boolean;
}

const signedInDestinations: Destination[] = [
    {to: '/explore', label: 'Explore', icon: CompassIcon, end: true},
    {to: '/myprojects', label: 'My projects', icon: LayoutGridIcon},
    {to: '/explore/groups', label: 'Groups', icon: UsersIcon},
    {to: '/assets', label: 'Assets', icon: CloudIcon}
];

const signedOutDestinations: Destination[] = [
    {to: '/explore', label: 'Explore', icon: CompassIcon, end: true},
    {to: '/explore/groups', label: 'Groups', icon: UsersIcon},
    {
        to: '/content',
        label: SiteContentConstants.ContentIndexPageTitle,
        icon: CircleHelpIcon
    }
];

/*
 * One header for every standard page. Four destinations; the page you're on
 * gets dark text and a purple underline. Personal and occasional items live
 * in the account menu. "New project" is the one primary button. On a phone
 * or narrow tablet it collapses to the logo mark, New project and a menu
 * sheet.
 */
const AppHeader: React.FC = () => {
    const {authData, logout, isAuthenticated} = useAuthStateManager();
    const {storageUri} = useClientConfig();
    const navigate = useNavigate();
    const sheet = useSidePanel('app-menu');

    const registered =
        isAuthenticated && authData.user_status === UserStatus.Registered;
    const mustRegister =
        isAuthenticated && authData.user_status === UserStatus.NotRegistered;
    const isAdmin =
        authData.roles.includes(RoleNames.Admin) ||
        authData.roles.includes(RoleNames.ContentEditor);
    const nickname = authData.nickname ?? '';
    const me = {
        nickname,
        name: authData.name,
        thumbnail: authData.id
            ? joinPath(storageUri, 'thumbnails', `${authData.id}.png`)
            : null
    };

    const destinations = registered
        ? signedInDestinations
        : mustRegister
          ? []
          : signedOutDestinations;

    const logOut = () => {
        sheet.close();
        logout();
        navigate('/');
    };

    const accountItems = [
        {label: 'Your portfolio', icon: UserRoundIcon, href: profileUrl(nickname)},
        {label: 'Settings', icon: SettingsIcon, href: '/profile'},
        {label: 'Connected apps', icon: PlugIcon, href: '/profile?tab=connectedapps'},
        ...(isAdmin ? [{label: 'Admin', icon: ShieldUserIcon, href: '/admin'}] : []),
        {kind: 'divider' as const},
        {label: 'Log out', icon: LogOutIcon, onSelect: logOut}
    ];

    const navLinkClass = ({isActive}: {isActive: boolean}) =>
        classNames('mx-app-link', {active: isActive});

    return (
        <header className="mx-app-header">
            <Link to="/" className="mx-app-logo" aria-label="Mixiply home">
                <img src="/images/logos/logo-default.svg" alt="" className="mx-app-logo-full" />
                <img src="/images/logos/logo-mark.svg" alt="" className="mx-app-logo-mark" />
            </Link>

            <nav aria-label="Main" className="mx-app-nav">
                {destinations.map(d => (
                    <NavLink key={d.to} to={d.to} end={d.end} className={navLinkClass}>
                        <Icon icon={d.icon} size="s" />
                        {d.label}
                    </NavLink>
                ))}
            </nav>

            <div className="mx-app-actions">
                {registered && (
                    <>
                        <Link
                            to="/content"
                            className="btn btn-tertiary btn-icon-only mx-app-desktop"
                            aria-label={SiteContentConstants.ContentIndexPageTitle}
                            title={SiteContentConstants.ContentIndexPageTitle}
                        >
                            <Icon icon={CircleHelpIcon} size="s" />
                        </Link>
                        <span className="mx-app-desktop">
                            <MxNotifications />
                        </span>
                        <Link to="/new-project" className="btn btn-primary mx-app-new">
                            <Icon icon={PlusIcon} size={18} strokeWidth={2} />
                            <span className="mx-app-new-label">New project</span>
                        </Link>
                        <span className="mx-app-desktop">
                            <Menu
                                label={`Account menu for ${atify(nickname)}`}
                                items={accountItems}
                                trigger={
                                    <span className="mx-app-account-trigger">
                                        <Avatar creator={me} size="s" />
                                        <Icon icon={ChevronDownIcon} size="xs" />
                                    </span>
                                }
                                className="mx-app-account"
                            />
                        </span>
                    </>
                )}
                {mustRegister && (
                    <>
                        <Link to="/registration" className="btn btn-primary">
                            Finish signing up
                        </Link>
                        <button type="button" className="btn btn-tertiary" onClick={logOut}>
                            Log out
                        </button>
                    </>
                )}
                {!isAuthenticated && (
                    <>
                        <Link to="/login" className="btn btn-secondary mx-app-desktop">
                            Log in
                        </Link>
                        <Link
                            to={`/login?redir=${encodeURIComponent('/new-project')}`}
                            className="btn btn-primary"
                        >
                            Start creating
                        </Link>
                    </>
                )}
                {!mustRegister && (
                    <button
                        type="button"
                        className="btn btn-tertiary btn-icon-only mx-app-menu-button"
                        aria-label="Open menu"
                        onClick={sheet.toggle}
                    >
                        <Icon icon={MenuIcon} size={22} />
                    </button>
                )}
            </div>

            <SidePanel id="app-menu" title="Menu" className="mx-app-sheet">
                <nav aria-label="Main" className="mx-app-sheet-nav">
                    {destinations.map(d => (
                        <NavLink
                            key={d.to}
                            to={d.to}
                            end={d.end}
                            className={navLinkClass}
                            onClick={sheet.close}
                        >
                            <Icon icon={d.icon} size="s" />
                            {d.label}
                        </NavLink>
                    ))}
                    {registered && (
                        <NavLink to="/content" className={navLinkClass} onClick={sheet.close}>
                            <Icon icon={CircleHelpIcon} size="s" />
                            {SiteContentConstants.ContentIndexPageTitle}
                        </NavLink>
                    )}
                </nav>
                {registered ? (
                    <div className="mx-app-sheet-account">
                        <div className="mx-app-sheet-identity">
                            <Avatar creator={me} size="m" />
                            <span>
                                <b>{authData.name || atify(nickname)}</b>
                                <span className="text-secondary">{atify(nickname)}</span>
                            </span>
                        </div>
                        <Link to={profileUrl(nickname)} className="mx-app-link" onClick={sheet.close}>
                            Your portfolio
                        </Link>
                        <Link to="/profile" className="mx-app-link" onClick={sheet.close}>
                            Settings
                        </Link>
                        <Link to="/profile?tab=connectedapps" className="mx-app-link" onClick={sheet.close}>
                            Connected apps
                        </Link>
                        {isAdmin && (
                            <Link to="/admin" className="mx-app-link" onClick={sheet.close}>
                                Admin
                            </Link>
                        )}
                        <button type="button" className="mx-app-link" onClick={logOut}>
                            Log out
                        </button>
                    </div>
                ) : (
                    !isAuthenticated && (
                        <div className="mx-app-sheet-account">
                            <Link to="/login" className="btn btn-secondary" onClick={sheet.close}>
                                Log in
                            </Link>
                        </div>
                    )
                )}
            </SidePanel>
        </header>
    );
};

export default AppHeader;
